import React from 'react';
import { Table, Descriptions, Tag, Badge } from 'antd';
const PHASE_ICONS = { fetch: '📡', dexie: '💾', loki: '🧠', cleanup: '🧹', done: '✅', error: '❌' };
const PROGRESS_STATUS = { pending: '⏸', writing: '⏳', done: '✅' };
const STATUS_COLORS = { done: '#52c41a', running: '#1890ff', error: '#f5222d' };
function formatDuration(ms) {
    if (ms === undefined || ms === 0)
        return '—';
    if (ms < 1000)
        return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}
function formatTime(ts) {
    if (!ts)
        return '—';
    return new Date(ts).toLocaleTimeString();
}
function renderDexieProgress(dexieProgress) {
    const entries = Object.entries(dexieProgress);
    if (entries.length === 0)
        return '—';
    return (React.createElement("span", null, entries.map(([tableName, p], idx) => {
        var _a;
        return (React.createElement("span", { key: tableName },
            idx > 0 && React.createElement("span", { style: { color: '#ccc', margin: '0 4px' } }, "/"),
            React.createElement("code", { style: { fontSize: 11 } }, tableName),
            ": ",
            p.written,
            "/",
            p.total,
            " ", (_a = PROGRESS_STATUS[p.status]) !== null && _a !== void 0 ? _a : '—'));
    })));
}
function renderLokiProgress(lokiProgress) {
    const entries = Object.entries(lokiProgress);
    if (entries.length === 0)
        return '—';
    return (React.createElement("span", null, entries.map(([collName, p], idx) => {
        var _a;
        return (React.createElement("span", { key: collName },
            idx > 0 && React.createElement("span", { style: { color: '#ccc', margin: '0 4px' } }, "/"),
            React.createElement("code", { style: { fontSize: 11 } }, collName),
            ": ",
            p.written,
            "/",
            p.total,
            " ", (_a = PROGRESS_STATUS[p.status]) !== null && _a !== void 0 ? _a : '—'));
    })));
}
const historyColumns = [
    {
        title: '#',
        dataIndex: 'index',
        key: 'index',
        width: 40,
        align: 'center',
    },
    {
        title: '触发方式',
        dataIndex: 'trigger',
        key: 'trigger',
        width: 80,
        render: (v) => React.createElement(Tag, null, v),
    },
    {
        title: '时间',
        dataIndex: 'startTime',
        key: 'startTime',
        width: 100,
        render: (ts) => formatTime(ts),
    },
    {
        title: '耗时',
        dataIndex: 'syncDuration',
        key: 'syncDuration',
        width: 80,
        render: (v) => formatDuration(v),
    },
    {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 70,
        render: (s) => { var _a; return React.createElement(Badge, { color: (_a = STATUS_COLORS[s]) !== null && _a !== void 0 ? _a : '#999', text: s }); },
    },
    {
        title: '个人联系人',
        dataIndex: 'personalCount',
        key: 'personalCount',
        width: 90,
        align: 'right',
        render: (v) => v.toLocaleString(),
    },
    {
        title: '企业联系人',
        dataIndex: 'enterpriseContactCount',
        key: 'enterpriseContactCount',
        width: 90,
        align: 'right',
        render: (v) => v.toLocaleString(),
    },
];
const taskColumns = [
    {
        title: '阶段',
        dataIndex: 'phase',
        key: 'phase',
        width: 100,
        render: (phase) => {
            var _a;
            return (React.createElement("span", null, (_a = PHASE_ICONS[phase]) !== null && _a !== void 0 ? _a : '—',
                " ",
                phase));
        },
    },
    {
        title: '服务端联系人',
        dataIndex: 'serverContactCount',
        key: 'serverContactCount',
        width: 110,
        align: 'right',
        render: (v) => v.toLocaleString(),
    },
    {
        title: '服务端组织',
        dataIndex: 'serverOrgCount',
        key: 'serverOrgCount',
        width: 100,
        align: 'right',
        render: (v) => v.toLocaleString(),
    },
    {
        title: 'Dexie 进度',
        key: 'dexie',
        render: (_, record) => renderDexieProgress(record.dexieProgress),
    },
    {
        title: 'Loki 进度',
        key: 'loki',
        render: (_, record) => renderLokiProgress(record.lokiProgress),
    },
];
const SyncStatusSection = ({ trackerSnapshot, enterpriseConfig, personalMarkStats, loading }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const summary = trackerSnapshot === null || trackerSnapshot === void 0 ? void 0 : trackerSnapshot.lastSyncSummary;
    const session = trackerSnapshot === null || trackerSnapshot === void 0 ? void 0 : trackerSnapshot.currentSession;
    const history = (_a = trackerSnapshot === null || trackerSnapshot === void 0 ? void 0 : trackerSnapshot.syncHistory) !== null && _a !== void 0 ? _a : [];
    return (React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        React.createElement(Descriptions, { title: "\u4F01\u4E1A\u901A\u8BAF\u5F55\u914D\u7F6E", size: "small", bordered: true, column: 3 },
            React.createElement(Descriptions.Item, { label: "needSync" }, (_b = enterpriseConfig === null || enterpriseConfig === void 0 ? void 0 : enterpriseConfig.needSync) !== null && _b !== void 0 ? _b : '—'),
            React.createElement(Descriptions.Item, { label: "hasAllContacts" }, (_c = enterpriseConfig === null || enterpriseConfig === void 0 ? void 0 : enterpriseConfig.hasAllContacts) !== null && _c !== void 0 ? _c : '—'),
            React.createElement(Descriptions.Item, { label: "syncInterval" }, enterpriseConfig ? `${enterpriseConfig.syncInterval} min` : '—'),
            React.createElement(Descriptions.Item, { label: "ruleVersion" }, (_d = enterpriseConfig === null || enterpriseConfig === void 0 ? void 0 : enterpriseConfig.ruleVersion) !== null && _d !== void 0 ? _d : '—'),
            React.createElement(Descriptions.Item, { label: "contactsVersion" }, (_e = enterpriseConfig === null || enterpriseConfig === void 0 ? void 0 : enterpriseConfig.contactsVersion) !== null && _e !== void 0 ? _e : '—'),
            React.createElement(Descriptions.Item, { label: "unitVersion" }, (_f = enterpriseConfig === null || enterpriseConfig === void 0 ? void 0 : enterpriseConfig.unitVersion) !== null && _f !== void 0 ? _f : '—')),
        summary && (React.createElement(Descriptions, { title: "\u4E0A\u6B21\u540C\u6B65\u6458\u8981", size: "small", bordered: true, column: 3 },
            summary.enterprise && (React.createElement(React.Fragment, null,
                React.createElement(Descriptions.Item, { label: "\u4F01\u4E1A\u8054\u7CFB\u4EBA" }, summary.enterprise.contactCount.toLocaleString()),
                React.createElement(Descriptions.Item, { label: "\u4F01\u4E1A\u7EC4\u7EC7" }, summary.enterprise.orgCount.toLocaleString()),
                React.createElement(Descriptions.Item, { label: "\u4F01\u4E1A\u540C\u6B65\u65F6\u95F4" }, formatTime(summary.enterprise.time)))),
            summary.personal && (React.createElement(React.Fragment, null,
                React.createElement(Descriptions.Item, { label: "\u4E2A\u4EBA\u8054\u7CFB\u4EBA" }, summary.personal.contactCount.toLocaleString()),
                React.createElement(Descriptions.Item, { label: "\u4E2A\u4EBA\u5206\u7EC4" }, (_j = (_h = (_g = summary.personalOrg) === null || _g === void 0 ? void 0 : _g.contactCount) === null || _h === void 0 ? void 0 : _h.toLocaleString()) !== null && _j !== void 0 ? _j : '—'),
                React.createElement(Descriptions.Item, { label: "\u4E2A\u4EBA\u540C\u6B65\u65F6\u95F4" }, formatTime(summary.personal.time)))),
            React.createElement(Descriptions.Item, { label: "\u603B\u8BA1" },
                summary.total.toLocaleString(),
                " \u6761"),
            React.createElement(Descriptions.Item, { label: "\u8017\u65F6" }, formatDuration(summary.syncDuration)),
            personalMarkStats && (React.createElement(Descriptions.Item, { label: "\u661F\u6807" },
                "\u8054\u7CFB\u4EBA: ",
                personalMarkStats.contactStars,
                " / \u5206\u7EC4: ",
                personalMarkStats.groupStars)))),
        trackerSnapshot && (React.createElement("div", { style: { fontSize: 12, color: '#666' } },
            "\u5B9A\u65F6\u540C\u6B65\u6B21\u6570: ",
            React.createElement(Tag, null, trackerSnapshot.timerSyncCount))),
        session && (React.createElement("div", null,
            React.createElement("div", { style: { marginBottom: 8, fontSize: 13, fontWeight: 500 } },
                React.createElement(Badge, { status: "processing" }),
                React.createElement("span", { style: { marginLeft: 4, color: (_k = STATUS_COLORS[session.status]) !== null && _k !== void 0 ? _k : '#333' } },
                    "\u5B9E\u65F6\u540C\u6B65 (",
                    session.status,
                    ")"),
                React.createElement("span", { style: { marginLeft: 12, color: '#999', fontWeight: 400 } },
                    "\u89E6\u53D1: ",
                    session.trigger,
                    " \u00B7 \u5F00\u59CB: ",
                    formatTime(session.startTime))),
            React.createElement(Table, { columns: taskColumns, dataSource: session.tasks, rowKey: (_, idx) => String(idx), pagination: false, size: "small", loading: loading }))),
        history.length > 0 && (React.createElement("div", null,
            React.createElement("div", { style: { marginBottom: 8, fontSize: 13, fontWeight: 500 } }, "\u540C\u6B65\u5386\u53F2"),
            React.createElement(Table, { columns: historyColumns, dataSource: history.map((entry, idx) => (Object.assign(Object.assign({}, entry), { index: idx + 1 }))), rowKey: (_, idx) => String(idx), pagination: false, size: "small", loading: loading, scroll: { y: 200 } }))),
        !summary && !session && history.length === 0 && !loading && React.createElement("div", { style: { padding: '16px 0', color: '#999', textAlign: 'center' } }, "\u6682\u65E0\u540C\u6B65\u6570\u636E")));
};
export default SyncStatusSection;
